// react
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// @mui
import { Box, Button, Divider, FormControlLabel, Grid, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
// utils
import { useSnackbar } from 'notistack';
import { AxiosPost } from '../../../../utils/axios';
import { fCentsToCoins } from '../../../../utils/formatNumber';
import { stringToTitle } from '../../../../utils/stringToTitle';
// components
import Iconify from '../../../../components/Iconify';
import Image from '../../../../components/Image';
import RedeemCodeInput from '../../../../components/payments/RedeemCodeInput';
// actions
import { clearCouponDiscount, validateCouponRequest } from '../../../../redux/shop/coupons/actions';
import { checkoutMembershipRequest } from '../../../../redux/shop/memberships/actions';
// images
import imgCard2 from '../../../../assets/icons/american.svg';
import imgCard from '../../../../assets/icons/mastercard.svg';
import imgOxxo from '../../../../assets/icons/oxxo.svg';
import imgVisa from '../../../../assets/icons/visa.svg';

// ----------------------------------------------------------------------

const PRODUCT_DESCRIPTION = [
    {
        title: 'Activacion',
        description: 'Confirmado el pago, se activara automaticamente en tu cuenta.',
        icon: 'ic:round-verified',
    },
    {
        title: 'Pago seguro',
        description: 'Todos nuestros metodos de pago estan verificados.',
        icon: 'ic:round-verified-user',
    },
    {
        title: 'Soporte',
        description: 'Si tienes alguna duda no dudes en contactarnos.',
        icon: 'material-symbols:contact-support',
    },
];

const RootStyle = styled('div')(({ theme }) => ({
    width: '100%',
    padding: theme.spacing(0, 3),
}));

const IconWrapperStyle = styled('div')(({ theme }) => ({
    color: theme.palette.primary.main,
    margin: 'auto',
    width: theme.spacing(8),
    height: theme.spacing(8),
    display: 'flex',
    alignItems: 'center',
    borderRadius: '50%',
    marginBottom: theme.spacing(3),
    justifyContent: 'center',
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)}`,
}));

// ----------------------------------------------------------------------

// Build a unified list of pricing options from all price arrays.
function buildPricingOptions(prices_subscription = [], prices_onetime = [], prices_oxxo = []) {
    const options = [];

    // One-time (card only — OXXO equivalent lives in prices_oxxo)
    prices_onetime.forEach((p) => {
        options.push({
            ...p,
            label: 'Pago único',
            sublabel: 'Una compra, sin renovación',
            type: 'one_time',
        });
    });

    // Monthly subscriptions (card)
    prices_subscription.forEach((p) => {
        const months = p.month || 1;
        let label;
        let sublabel;
        if (months === 1) {
            label = 'Suscripción mensual';
            sublabel = 'Se renueva cada mes — la cancelas cuando quieras';
        } else if (months === 6) {
            label = 'Suscripción semestral';
            sublabel = 'Se renueva cada 6 meses';
        } else {
            label = `Suscripción ${months} meses`;
            sublabel = `Se renueva cada ${months} meses`;
        }
        options.push({
            ...p,
            label,
            sublabel,
            type: 'subscription',
        });
    });

    return options;
}

export default function MembershipDetail({ data }) {
    // hooks
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // redux
    const { checkoutUrl } = useSelector((state) => state.shop.memberships);
    const { discount, errorCoupon } = useSelector((state) => state.shop.coupons);

    useEffect(() => {
        if (checkoutUrl) {
            window.location.href = checkoutUrl;
        }
    }, [checkoutUrl, discount]);

    // Reset any lingering coupon discount / error whenever we mount for a new
    // product. Without this, a discount validated for one product leaks into
    // the Total shown for the next one because `discount` lives in redux and
    // never clears on its own.
    useEffect(() => {
        dispatch(clearCouponDiscount());
        return () => { dispatch(clearCouponDiscount()); };
    }, [dispatch, data?.product_id]);

    // variables
    const {
        name, images, prices_subscription, prices_oxxo, prices_onetime = [],
        perk, description,
    } = data;

    // Build unified pricing options
    const pricingOptions = buildPricingOptions(
        prices_subscription, prices_onetime, prices_oxxo,
    );

    // states
    const [selected, setSelected] = useState(pricingOptions[0] || null);
    const [payMethod, setPayMethod] = useState('card');
    const [couponCode, setCouponCode] = useState('');

    // When paying with OXXO, find the matching OXXO price for the same plan
    // (same purchase_type + same duration). prices_oxxo contains entries for
    // both subscriptions AND one-time purchases since OXXO is a payment-method
    // toggle, not a separate plan.
    const getEffectivePrice = useCallback(() => {
        if (payMethod === 'oxxo' && selected && prices_oxxo?.length) {
            const oxxoMatch = prices_oxxo.find(
                (p) => p.month === (selected.month || 0)
                    && (p.purchase_type || 'subscription') === selected.type,
            );
            if (oxxoMatch) return oxxoMatch;
        }
        return selected;
    }, [payMethod, selected, prices_oxxo]);

    // Does the currently-selected plan have an OXXO equivalent?
    const oxxoAvailableForSelected = Boolean(
        selected && prices_oxxo?.some(
            (p) => p.month === (selected.month || 0)
                && (p.purchase_type || 'subscription') === selected.type,
        ),
    );

    // PayPal available for both one-time purchases (Orders v2) and
    // subscriptions (Subscriptions v1). The backend routes by purchase_type.
    const paypalAvailableForSelected = Boolean(selected);

    useEffect(() => {
        if (payMethod === 'paypal' && !paypalAvailableForSelected) {
            setPayMethod('card');
        }
    }, [payMethod, paypalAvailableForSelected]);

    const handleApplyCouponCode = useCallback(() => {
        const price = getEffectivePrice();
        if (couponCode && price) {
            dispatch(validateCouponRequest({
                price_id: price.price_id,
                month: price.month || 0,
                method: payMethod,
                perk,
                coupon_code: couponCode,
            }));
        }
    }, [couponCode, getEffectivePrice, perk, payMethod, dispatch]);

    const handlePay = async () => {
        try {
            const price = getEffectivePrice();
            if (!price) return enqueueSnackbar('Selecciona un plan', { variant: 'error' });

            // PayPal — REST API directo, redirect al approve_url de PayPal.
            // Distintos endpoints según el tipo de compra:
            //   one_time     → /paypal/create_order/       (Orders v2)
            //   subscription → /paypal/create_subscription/ (Subscriptions v1)
            if (payMethod === 'paypal') {
                const endpoint = selected?.type === 'subscription'
                    ? '/paypal/create_subscription/'
                    : '/paypal/create_order/';
                const resp = await AxiosPost(endpoint, {
                    price_id: price.price_id,
                    perk,
                    brand: 'holistiqum',
                });
                const approveUrl = resp.data?.response?.approve_url;
                if (!approveUrl) {
                    return enqueueSnackbar('No pudimos iniciar el pago con PayPal', { variant: 'error' });
                }
                window.location.href = approveUrl;
                return;
            }

            // Stripe path (card / oxxo)
            dispatch(checkoutMembershipRequest({
                price_id: price.price_id,
                month: price.month || 0,
                method: payMethod,
                perk,
                coupon_code: selected?.type === 'subscription' ? couponCode : '',
                purchase_type: price.purchase_type || selected?.type || 'subscription',
            }));
        } catch (e) {
            return enqueueSnackbar('Error al generar el pago', { variant: 'error' });
        }
    };

    // Display price
    const effectivePrice = getEffectivePrice();
    const displayAmount = effectivePrice?.unit_amount;

    return (
        <>
            <Grid container spacing={5}>
                <Grid item xs={12} md={6} lg={6}>
                    <Image alt="membership image" src={images?.at(0)} ratio="1/1" sx={{ borderRadius: 2 }} />
                </Grid>

                <Grid container item xs={12} md={6} lg={6}>
                    <RootStyle>
                        <Stack spacing={3}>
                            <Stack spacing={2}>
                                <Typography variant="h3">
                                    {stringToTitle(name)}
                                </Typography>

                                {description && (
                                    <Typography variant="body1" color="text.secondary">
                                        {description}
                                    </Typography>
                                )}

                                {/* Plan selection */}
                                {pricingOptions.length > 1 && (
                                    <Stack spacing={1.5}>
                                        <Typography variant="subtitle1">Elige tu plan</Typography>
                                        <Stack spacing={1}>
                                            {pricingOptions.map((option) => (
                                                <Button
                                                    key={option.price_id}
                                                    onClick={() => setSelected(option)}
                                                    variant={selected?.price_id === option.price_id ? 'contained' : 'outlined'}
                                                    fullWidth
                                                    sx={{
                                                        justifyContent: 'space-between',
                                                        py: 1.5,
                                                        px: 2,
                                                        textTransform: 'none',
                                                    }}
                                                >
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="subtitle2">{option.label}</Typography>
                                                    </Stack>
                                                    <Typography variant="subtitle1">
                                                        {fCentsToCoins(option.unit_amount)}
                                                    </Typography>
                                                </Button>
                                            ))}
                                        </Stack>
                                    </Stack>
                                )}

                                {/* Single option - just show the price */}
                                {pricingOptions.length === 1 && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="subtitle1">{pricingOptions[0].label}</Typography>
                                        <Typography variant="h4">{fCentsToCoins(pricingOptions[0].unit_amount)}</Typography>
                                    </Stack>
                                )}

                                <Divider sx={{ borderStyle: 'dashed' }} />

                                {/* Payment method toggle — shown whenever the
                                    selected plan has an OXXO or PayPal
                                    alternative. PayPal applies only to
                                    one-time purchases in phase 1. */}
                                {(oxxoAvailableForSelected || paypalAvailableForSelected) && (
                                    <Stack spacing={2.5}>
                                        <RadioGroup
                                            value={payMethod}
                                            onChange={(e) => setPayMethod(e.target.value)}
                                        >
                                            <Stack spacing={2}>
                                                <Typography variant="subtitle1">Metodo de pago</Typography>

                                                <FormControlLabel
                                                    value="card"
                                                    control={<Radio size="small" />}
                                                    sx={{ '& > .MuiTypography-root': { width: '100%' } }}
                                                    label={
                                                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                            <Typography variant="body2">
                                                                Tarjeta de credito y debito
                                                            </Typography>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Image alt="card" sx={{ height: 50, width: 50 }} src={imgVisa} />
                                                                <Image alt="card" sx={{ height: 50, width: 50 }} src={imgCard} />
                                                                <Image alt="card" sx={{ height: 40, width: 40 }} src={imgCard2} />
                                                            </Stack>
                                                        </Stack>
                                                    }
                                                />

                                                {oxxoAvailableForSelected && (
                                                    <Stack spacing={0.5}>
                                                        <FormControlLabel
                                                            value="oxxo"
                                                            control={<Radio size="small" />}
                                                            sx={{ '& > .MuiTypography-root': { width: '100%' } }}
                                                            label={
                                                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                                    <Typography variant="body2">
                                                                        Pago en OXXO
                                                                    </Typography>
                                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                                        <Image alt="oxxo" sx={{ height: 20, width: 'auto' }} src={imgOxxo} />
                                                                    </Stack>
                                                                </Stack>
                                                            }
                                                        />
                                                        {selected?.type === 'subscription' && (
                                                            <Typography variant="caption" sx={{ color: 'text.secondary', pl: 4 }}>
                                                                Pago en efectivo. La renovación no es automática: cada mes recibirás un nuevo voucher por correo para pagarlo en OXXO.
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                )}

                                                {paypalAvailableForSelected && (
                                                    <FormControlLabel
                                                        value="paypal"
                                                        control={<Radio size="small" />}
                                                        sx={{ '& > .MuiTypography-root': { width: '100%' } }}
                                                        label={
                                                            <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                                <Typography variant="body2">
                                                                    PayPal
                                                                </Typography>
                                                                <Iconify icon="logos:paypal" width={60} height={24} />
                                                            </Stack>
                                                        }
                                                    />
                                                )}
                                            </Stack>
                                        </RadioGroup>
                                        <Divider sx={{ borderStyle: 'dashed' }} />
                                    </Stack>
                                )}
                            </Stack>

                            <Stack spacing={3}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h4">Total</Typography>
                                    <Typography variant="h3">
                                        &nbsp;{selected?.type === 'subscription' && discount != null
                                            ? fCentsToCoins(discount)
                                            : fCentsToCoins(displayAmount)}
                                    </Typography>
                                </Stack>

                                {errorCoupon && (
                                    <Typography variant="body2" sx={{ color: 'error.main' }}>
                                        {errorCoupon}
                                    </Typography>
                                )}

                                <Stack spacing={2}>
                                    {selected?.type === 'subscription' && (
                                        <Stack direction="row" spacing={2}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                placeholder="Codigo promocional"
                                                onBlur={handleApplyCouponCode}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleApplyCouponCode();
                                                }}
                                                onChange={(e) => setCouponCode(e.target.value)}
                                            />
                                            <Button variant="outlined" color="primary" onClick={handleApplyCouponCode}>
                                                <Iconify icon="solar:tag-linear" height={19} width={19} />
                                            </Button>
                                        </Stack>
                                    )}
                                    <RedeemCodeInput
                                        paymentProvider={payMethod === 'paypal' ? 'paypal' : 'stripe'}
                                    />
                                    <Button fullWidth size="large" variant="contained" onClick={handlePay}>
                                        Comprar
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </RootStyle>
                </Grid>
            </Grid>

            <Grid container sx={{ mt: 8 }}>
                {PRODUCT_DESCRIPTION.map((item) => (
                    <Grid item xs={12} md={4} key={item.title}>
                        <Box sx={{ my: 2, mx: 'auto', maxWidth: 280, textAlign: 'center' }}>
                            <IconWrapperStyle>
                                <Iconify icon={item.icon} width={36} height={36} />
                            </IconWrapperStyle>
                            <Typography variant="subtitle1" gutterBottom>
                                {item.title}
                            </Typography>
                            <Typography sx={{ color: 'text.secondary' }}>{item.description}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </>
    );
}
